import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  ArrowRightLeft,
  BookOpen,
  Calculator,
  ClipboardList,
  Code2,
  GanttChart,
  GitBranch,
  MessageSquare,
  Target,
  TrendingUp,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/serge'
import {
  DELIVERABLE_LABELS,
  DELIVERABLE_TYPES,
} from '@/services/sergeApi'

export type DeliverableType = keyof typeof DELIVERABLE_TYPES

export interface Deliverable {
  id: number
  type: DeliverableType
  status: 'pending' | 'generating' | 'draft' | 'review' | 'validated'
  updatedAt?: string
}

export interface DeliverableTypeInfo {
  key: DeliverableType
  label: string
}

export interface DeliverableItem {
  type: DeliverableType
  label: string
  description: string
  icon: LucideIcon
  primary?: boolean
  deliverable?: Deliverable
}

const HIDDEN_TYPES: DeliverableType[] = ['workflow']

const TYPE_META: Record<DeliverableType, { description: string; icon: LucideIcon; primary?: boolean }> = {
  spec_functional: { description: 'Exigences metier structurees', icon: ClipboardList, primary: true },
  spec_technical: { description: 'Architecture, APIs, BDD', icon: Code2, primary: true },
  diagram_class: { description: 'UML class diagram', icon: GitBranch },
  diagram_sequence: { description: 'UML sequence diagram', icon: ArrowRightLeft },
  diagram_architecture: { description: 'Architecture système (D2/Excalidraw)', icon: Workflow },
  user_stories: { description: 'Backlog des besoins utilisateurs', icon: ClipboardList },
  risk_register: { description: 'Registre et suivi des risques', icon: Target },
  workflow: { description: 'Processus type Miro', icon: Workflow },
  market_study: { description: 'Analyse concurrentielle', icon: TrendingUp },
  technical_dossier: { description: 'Synthese technique', icon: BookOpen },
  communication_plan: { description: 'Plan com interne/externe', icon: MessageSquare },
  sales_strategy: { description: 'Go-to-market', icon: Target },
  budget: { description: 'Couts et projections', icon: Calculator },
  gantt: { description: 'Diagramme Gantt (image)', icon: GanttChart },
  gantt_sheets: { description: 'Gantt interactif Google Sheets', icon: GanttChart, primary: true },
  planning: { description: 'Macro-planning', icon: GanttChart },
}

export interface DeliverablesGridProps {
  className?: string
  projectId?: number
  allowGenerate?: boolean
  deliverables?: Deliverable[]
  onGenerate?: (type: DeliverableType) => void
  isGenerating?: boolean
}

const statusLabels: Record<Deliverable['status'], string> = {
  pending: 'En attente',
  generating: 'Generation',
  draft: 'Brouillon',
  review: 'En revue',
  validated: 'Valide',
}

export default function DeliverablesGrid({
  className,
  projectId,
  allowGenerate = true,
  deliverables = [],
  onGenerate,
  isGenerating = false,
}: DeliverablesGridProps) {
  const fallbackTypes = useMemo(
    () =>
      Object.entries(DELIVERABLE_LABELS).map(([key, label]) => ({
        key: key as DeliverableType,
        label,
      })).filter((item) => !HIDDEN_TYPES.includes(item.key)),
    []
  )

  const items: DeliverableItem[] = fallbackTypes.map((typeInfo) => {
    const meta = TYPE_META[typeInfo.key]
    const deliverable = deliverables.find((item) => item.type === typeInfo.key)
    return {
      type: typeInfo.key,
      label: typeInfo.label,
      description: meta?.description ?? 'Livrable SERGE',
      icon: meta?.icon ?? ClipboardList,
      primary: meta?.primary,
      deliverable,
    }
  })

  return (
    <div className={cn('border border-border bg-surface p-5', className)}>
      <div className="flex items-center justify-between border-b border-border pb-3">
        <span className="text-sm font-mono text-foreground">Livrables</span>
        <div className="flex items-center gap-3 text-xs font-mono text-foreground-muted">
          {!allowGenerate && <span>Lecture seule</span>}
          {`${items.length} types`}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((item) => (
          <div
            key={item.type}
            className={cn(
              'border border-border bg-bg p-4 transition-colors',
              item.primary ? 'border-primary/40 bg-primary/5' : 'hover:border-border-hover'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <item.icon className={cn('w-4 h-4', item.primary ? 'text-primary' : 'text-foreground-muted')} />
                <span className={cn('text-sm font-mono', item.primary ? 'text-primary' : 'text-foreground')}>
                  {item.label}
                </span>
              </div>
              {item.primary && <span className="text-xs font-mono text-primary">Core</span>}
            </div>
            <p className="text-xs text-foreground-secondary mt-2">{item.description}</p>
            <div className="mt-3 flex items-center justify-between text-xs font-mono text-foreground-muted">
              <span>
                {item.deliverable
                  ? statusLabels[item.deliverable.status]
                  : 'Non genere'}
              </span>
              {item.deliverable?.updatedAt && (
                <span>{new Date(item.deliverable.updatedAt).toLocaleDateString('fr-FR')}</span>
              )}
            </div>
            {allowGenerate && onGenerate && (
              <Button
                variant={item.primary ? 'primary' : 'outline'}
                size="sm"
                className="mt-3 w-full"
                onClick={() => onGenerate(item.type)}
                disabled={!projectId || isGenerating}
              >
                {isGenerating
                  ? 'Generation...'
                  : item.deliverable
                    ? 'Regenerer'
                    : 'Generer'}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
